/* eslint-disable no-console */
/**
 * Algolia Logger
 * Comprehensive logging for all Algolia requests and responses
 * Provides detailed debugging information for troubleshooting
 */

class AlgoliaLogger {
  constructor() {
    this.enabled = window.algoliaConfig?.debug || false;
    this.requestCount = 0;
    this.startTime = Date.now();
  }

  /**
   * Enable or disable logging
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    this.log('Logging ' + (enabled ? 'enabled' : 'disabled'));
  }

  /**
   * Log a message with timestamp
   */
  log(message, ...args) {
    if (!this.enabled) return;
    const elapsed = ((Date.now() - this.startTime) / 1000).toFixed(2);
    console.log(`[Algolia ${elapsed}s]`, message, ...args);
  }

  /**
   * Log a warning
   */
  warn(message, ...args) {
    if (!this.enabled) return;
    const elapsed = ((Date.now() - this.startTime) / 1000).toFixed(2);
    console.warn(`[Algolia ${elapsed}s]`, message, ...args);
  }

  /**
   * Log an error (always logs, regardless of enabled state)
   */
  error(message, ...args) {
    const elapsed = ((Date.now() - this.startTime) / 1000).toFixed(2);
    console.error(`[Algolia ${elapsed}s]`, message, ...args);
  }

  /**
   * Log an outgoing API request
   */
  logRequest(source, params) {
    if (!this.enabled) return;

    this.requestCount++;
    const requestId = this.requestCount;

    console.group(`[Algolia Request #${requestId}] ${source}`);
    console.log('Timestamp:', new Date().toISOString());
    console.log('Index:', params.indexName || params.index || 'N/A');
    console.log('Query:', params.query || '(empty)');

    if (params.filters) {
      console.log('Filters:', params.filters);
    }
    if (params.facetFilters) {
      console.log('Facet Filters:', params.facetFilters);
    }
    if (params.hitsPerPage) {
      console.log('Hits Per Page:', params.hitsPerPage);
    }
    if (params.page !== undefined) {
      console.log('Page:', params.page);
    }
    if (params.attributesToRetrieve) {
      console.log('Attributes to Retrieve:', params.attributesToRetrieve);
    }
    if (params.attributesToHighlight) {
      console.log('Attributes to Highlight:', params.attributesToHighlight);
    }

    console.log('Full Params:', params);
    console.groupEnd();

    return requestId;
  }

  /**
   * Log an API response
   */
  logResponse(source, requestId, results, duration) {
    if (!this.enabled) return;

    console.group(`[Algolia Response #${requestId}] ${source}`);
    console.log('Duration:', duration + 'ms');

    if (Array.isArray(results)) {
      // Multi-index response
      results.forEach((result, idx) => {
        this.logSingleResult(result, idx);
      });
    } else {
      this.logSingleResult(results, 0);
    }

    console.groupEnd();
  }

  /**
   * Log a single result set
   */
  logSingleResult(result, index) {
    if (!result) {
      console.log(`Result ${index}: null/undefined`);
      return;
    }

    console.group(`Result Set ${index}: ${result.index || 'unknown index'}`);
    console.log('Total Hits:', result.nbHits);
    console.log('Hits Returned:', result.hits?.length || 0);
    console.log('Page:', result.page);
    console.log('Total Pages:', result.nbPages);
    console.log('Processing Time:', result.processingTimeMS + 'ms');
    console.log('Query:', result.query || '(empty)');

    if (result.hits && result.hits.length > 0) {
      console.log('First 3 hits:');
      result.hits.slice(0, 3).forEach((hit, i) => {
        console.log(`  ${i + 1}. ${hit.title || hit.name || hit.handle || hit.objectID}`);
      });

      if (result.hits.length > 3) {
        console.log(`  ... and ${result.hits.length - 3} more`);
      }
    }

    if (result.facets) {
      console.log('Facets:', Object.keys(result.facets));
    }

    console.groupEnd();
  }

  /**
   * Log an error response
   */
  logError(source, requestId, error, duration) {
    console.group(`[Algolia Error #${requestId}] ${source}`);
    console.error('Duration:', duration + 'ms');
    console.error('Error:', error.message || error);
    console.error('Stack:', error.stack);
    console.groupEnd();
  }

  /**
   * Create a wrapped search client that logs all requests
   */
  wrapSearchClient(searchClient, source = 'Search') {
    const logger = this;

    return {
      ...searchClient,
      search: async function(requests) {
        const startTime = performance.now();

        // Log each request
        const requestIds = requests.map((req, idx) => {
          return logger.logRequest(`${source} [${idx}]`, {
            indexName: req.indexName,
            query: req.query,
            ...req.params
          });
        });

        try {
          const response = await searchClient.search(requests);
          const duration = Math.round(performance.now() - startTime);

          // Log each response
          if (response.results) {
            response.results.forEach((result, idx) => {
              logger.logResponse(`${source} [${idx}]`, requestIds[idx], result, duration);
            });
          }

          return response;
        } catch (error) {
          const duration = Math.round(performance.now() - startTime);
          requestIds.forEach(id => {
            logger.logError(source, id, error, duration);
          });
          throw error;
        }
      },

      searchForFacetValues: async function(requests) {
        const startTime = performance.now();
        const requestId = logger.logRequest(`${source} Facets`, requests);

        try {
          const response = await searchClient.searchForFacetValues(requests);
          const duration = Math.round(performance.now() - startTime);
          logger.logResponse(`${source} Facets`, requestId, response, duration);
          return response;
        } catch (error) {
          const duration = Math.round(performance.now() - startTime);
          logger.logError(`${source} Facets`, requestId, error, duration);
          throw error;
        }
      }
    };
  }

  /**
   * Create InstantSearch middleware for logging
   */
  createInstantSearchMiddleware(source = 'InstantSearch') {
    const logger = this;
    const _requestId = 0;
    const _requestStartTime = 0;

    return () => {
      return {
        onStateChange({ uiState }) {
          if (logger.enabled) {
            logger.log(`${source} State Change:`, uiState);
          }
        },

        subscribe() {},
        unsubscribe() {},
      };
    };
  }

  /**
   * Log InstantSearch render event
   */
  logInstantSearchRender(source, results) {
    if (!this.enabled) return;

    console.group(`[Algolia Render] ${source}`);

    if (results) {
      console.log('Total Hits:', results.nbHits);
      console.log('Hits Displayed:', results.hits?.length || 0);
      console.log('Query:', results.query || '(empty)');
      console.log('Processing Time:', results.processingTimeMS + 'ms');
    }

    console.groupEnd();
  }

  /**
   * Log configuration on initialization
   */
  logConfig(config) {
    if (!this.enabled) return;

    console.group('[Algolia Configuration]');
    console.log('App ID:', config.appId);
    console.log('Index Name:', config.indexName);
    console.log('Collections Index:', config.collectionsIndex || 'N/A');
    console.log('Suggestions Index:', config.suggestionsIndex || 'N/A');
    console.log('Debug Mode:', config.debug);
    console.log('Features:', config.features);
    console.log('Search Parameters:', config.searchParameters);
    console.groupEnd();
  }

  /**
   * Get summary stats
   */
  getStats() {
    return {
      requestCount: this.requestCount,
      uptime: ((Date.now() - this.startTime) / 1000).toFixed(2) + 's'
    };
  }

  /**
   * Print summary to console
   */
  printSummary() {
    const stats = this.getStats();
    console.group('[Algolia Logger Summary]');
    console.log('Total Requests:', stats.requestCount);
    console.log('Uptime:', stats.uptime);
    console.groupEnd();
  }
}

// Create global instance
window.algoliaLogger = new AlgoliaLogger();

// Auto-enable if debug is set
if (window.algoliaConfig?.debug) {
  window.algoliaLogger.setEnabled(true);
  window.algoliaLogger.logConfig(window.algoliaConfig);
}

// Export for module use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AlgoliaLogger;
}
